import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Spinner } from '@fluentui/react-components'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { requestFn } from '@openapi-qraft/react'
import { ENDPOINTS } from '@/util'
import { useState } from 'react'
import { useTokenValidator } from '@/hooks/useTokenValidator'
import { useSessionStorage } from '@/hooks/useSessionStorage'

export type ImageForm = {
  filename: string
  file: File | null
}

type AddImageFormProps = {
  onSubmit: () => void
}

export const AddImageForm = (props: AddImageFormProps) => {
  // ✅ Add local loading state
  const [isLoading, setIsLoading] = useState(false)

  // ✅ Get the token from session storage
  const [token] = useSessionStorage('token', '')

  // ✅ Use the token validator
  const { isTokenExpired } = useTokenValidator()

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ImageForm>({
    defaultValues: {
      filename: '',
      file: null,
    },
  })

  const onSubmit = async (data: ImageForm) => {
    if (!data.file) {
      toast.error('File is required')
      return
    }

    if (!token || isTokenExpired()) {
      toast.error('Your session has expired. Please log in again.')
      sessionStorage.removeItem('token')
      window.location.href = '/login'
      return
    }

    // ✅ Start the spinner
    setIsLoading(true)

    const formData = new FormData()
    formData.append('filename', data.filename)
    formData.append('file', data.file)

    try {
      await requestFn(
        {
          method: 'post',
          url: '/rest/support/addImage',
          mediaType: 'multipart/form-data',
        },
        {
          baseUrl: ENDPOINTS.DOTNET,
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      toast.success('Image successfully added')
      props.onSubmit()
    } catch (error) {
      console.error('Error in adding image:', error)
      toast.error('Error in adding image.')
    } finally {
      // ✅ Stop the spinner
      setIsLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // ✅ Validate file size (6 MB limit)
      if (file.size > 6 * 1024 * 1024) {
        toast.error('File size exceeds the 6 MB limit.')
        return
      }

      setValue('file', file)

      // ✅ Automatically set the filename if it’s not manually provided
      if (!getValues('filename')) {
        setValue('filename', file.name)
      }
    }
  }

  return (
    <div className="form-wrapper add-image-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Field
          label="Filename"
          required
          validationState={errors.filename ? 'error' : 'none'}
          validationMessage={errors.filename?.message}
        >
          <Input {...register('filename', { required: 'Filename is required' })} placeholder="Enter filename" />
        </Field>
        <br />
        <Field
          label="Upload Image"
          required
          validationState={errors.file ? 'error' : 'none'}
          validationMessage={errors.file?.message}
        >
          <input type="file" accept="image/*" onChange={handleFileChange} />
        </Field>

        <div className="form-action-container">
          {isLoading ? (
            <Spinner size="medium" label="Uploading..." />
          ) : (
            <Button text="Submit" type="submit" onClick={handleSubmit(onSubmit)} disabled={isLoading} />
          )}
        </div>
      </form>
    </div>
  )
}
